import { useRef, Fragment } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { MaskedText, Reveal } from "@/components/motion/Reveal";
import Reel from "@/components/Reel";
import { OpenableImage } from "@/components/MediaViewer";
import { MARQUEE_ITEMS, APPROACH } from "@/data/content";
import { HERO_VIDEO, HOME_REELS, HOME_STILLS, FEATURED_REEL, BTS_REELS, GALLERIES } from "@/data/media";

const EASE = [0.16, 1, 0.3, 1];
const HERO_ROLES = ["Designer", "Collaborator", "Stylist", "Artist"];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const nameY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero" className="relative flex min-h-[88svh] flex-col items-center justify-center px-6 pb-14 pt-24 md:pt-28">
      <div className="relative flex w-full flex-col items-center text-center">
        <motion.div style={{ y: nameY }} className="w-full max-w-[1200px]">
          <motion.h1
            initial="hidden"
            animate="visible"
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 font-display font-extralight leading-[1.12] tracking-tight text-ink text-[8.4vw] sm:text-[6.2vw] lg:text-[4vw]"
          >
            <span className="sr-only">{HERO_ROLES.join(", ")}</span>
            {HERO_ROLES.map((w, i) => (
              <Fragment key={w}>
                {i > 0 && <span aria-hidden className="text-[0.3em] text-clay/70">●</span>}
                <motion.span
                  aria-hidden="true"
                  className="inline-block"
                  variants={{ hidden: { opacity: 0, y: 26 }, visible: { opacity: 1, y: 0 } }}
                  transition={{ duration: 0.9, ease: EASE, delay: 0.15 + i * 0.1 }}
                >
                  {w}
                </motion.span>
              </Fragment>
            ))}
          </motion.h1>
        </motion.div>

        <motion.div style={{ y: videoY }} className="relative z-10 mt-7 w-[52vw] max-w-[240px] md:mt-9 md:max-w-[280px]">
          <Reel {...HERO_VIDEO} ratio="aspect-[3/4]" reveal={false} />
        </motion.div>
      </div>
    </section>
  );
}

function DelicateMarquee() {
  return (
    <div data-testid="home-marquee" className="border-y border-hairline py-6">
      <Marquee speed={30} gradient={false} autoFill>
        {MARQUEE_ITEMS.map((word, i) => (
          <span key={i} className="mx-6 inline-flex items-center gap-6 font-display text-xl font-light tracking-wide text-muted md:text-2xl">
            {word}
            <span className="text-clay/60">—</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function StillTile({ src, alt, className }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.9, ease: EASE }}
      className={className || ""}
    >
      <OpenableImage src={src} alt={alt} label="Still" imgClass="aspect-[3/4]" />
    </motion.div>
  );
}

function InMotion() {
  const [a, b, c, d, e, f] = HOME_REELS;
  const [s0, s1] = HOME_STILLS;
  const mobile = [a, s0 && { still: s0 }, b, c, s1 && { still: s1 }, d, e, f].filter(Boolean);
  return (
    <section data-testid="home-motion" className="px-6 py-16 md:px-12 md:py-24">
      <div className="mx-auto max-w-[1400px]">
        <Reveal className="mb-12 text-center md:mb-20">
          <p className="overline text-muted">Social Media</p>
        </Reveal>

        {/* Desktop: featured reel centered & larger, stills interspersed with the reels */}
        <div className="hidden gap-6 md:grid md:grid-cols-[1fr_1.55fr_1fr] md:items-center lg:gap-8">
          <div className="flex flex-col gap-6 lg:gap-8">
            <Reel {...a} label="Reel" ratio="aspect-[3/4]" />
            {s0 && <StillTile {...s0} />}
            <Reel {...b} label="Reel" ratio="aspect-[3/4]" />
          </div>
          <div className="flex flex-col gap-6 lg:gap-8">
            <Reel {...FEATURED_REEL} label="Featured" ratio="aspect-[3/4]" />
            <div className="grid grid-cols-2 gap-6 lg:gap-8">
              <Reel {...e} label="Reel" ratio="aspect-[3/4]" />
              <Reel {...f} label="Reel" ratio="aspect-[3/4]" />
            </div>
          </div>
          <div className="flex flex-col gap-6 lg:gap-8">
            <Reel {...c} label="Reel" ratio="aspect-[3/4]" />
            {s1 && <StillTile {...s1} />}
            <Reel {...d} label="Reel" ratio="aspect-[3/4]" />
          </div>
        </div>

        {/* Mobile: featured full-width, then reels + stills interleaved */}
        <div className="grid grid-cols-2 gap-4 md:hidden">
          <div className="col-span-2">
            <Reel {...FEATURED_REEL} label="Featured" ratio="aspect-[3/4]" />
          </div>
          {mobile.map((item, i) =>
            item.still ? (
              <StillTile key={`still-${i}`} {...item.still} />
            ) : (
              <Reel key={item.id} {...item} label="Reel" ratio="aspect-[3/4]" />
            )
          )}
        </div>

        <Reveal className="mt-14 flex justify-center md:mt-20">
          <Link
            to="/reels"
            data-testid="more-reels-link"
            className="group inline-flex items-center gap-3 border border-ink/40 px-8 py-4 transition-colors duration-500 hover:border-ink"
          >
            <span className="overline">More Social Media Videos</span>
            <span className="transition-transform duration-500 group-hover:translate-x-1.5">↗</span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Stills() {
  return null;
}

function BehindScenes() {
  return (
    <section data-testid="home-bts" className="border-t border-hairline bg-paper2 px-6 py-16 md:px-12 md:py-24">
      <div className="mx-auto max-w-[1400px]">
        <div className="mb-10 flex flex-col gap-4 md:mb-14 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <p className="overline mb-5 text-muted">(Studio)</p>
            <h2 className="font-display text-3xl font-extralight tracking-tight text-ink md:text-5xl">Behind the Scenes</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/studio"
              data-testid="home-bts-link"
              className="group inline-flex items-center gap-3 self-start border-b border-ink/30 pb-1 transition-colors duration-500 hover:border-ink md:self-end"
            >
              <span className="overline">Enter the Studio</span>
              <span className="transition-transform duration-500 group-hover:translate-x-1.5">↗</span>
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
          {BTS_REELS.slice(0, 2).map((r, i) => (
            <Reel key={r.id} {...r} index={i + 1} label="BTS" ratio="aspect-video" />
          ))}
        </div>
      </div>
    </section>
  );
}

function Approach() {
  return (
    <section data-testid="home-approach" className="border-t border-hairline bg-paper2 px-6 py-16 md:px-12 md:py-24">
      <div className="mx-auto max-w-[1400px]">
        <Reveal>
          <p className="overline mb-10 text-muted">(How I Work)</p>
        </Reveal>
        <div className="grid gap-x-14 gap-y-10 md:grid-cols-2">
          {APPROACH.map((item, i) => (
            <Reveal key={item.no} delay={i * 0.05} className="flex gap-6 border-t border-ink/10 pt-6">
              <span className="font-mono text-sm text-clay">{item.no}</span>
              <div>
                <h3 className="font-display text-2xl font-extralight tracking-tight text-ink md:text-3xl">{item.title}</h3>
                <p className="mt-4 max-w-md font-body text-sm leading-relaxed text-stone">{item.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function GalleryTeaser() {
  const order = ["editorial", "jewelry", "product"];
  const previewOverride = { product: "dbx-41" };
  const teasers = order.map((id) => {
    const g = GALLERIES.find((x) => x.id === id);
    const img = previewOverride[id]
      ? g.images.find((im) => im.src.includes(previewOverride[id])) || g.images[0]
      : g.images[0];
    return { ...img, title: g.title };
  });
  return (
    <section data-testid="home-gallery-teaser" className="border-t border-hairline px-6 py-24 md:px-12 md:py-40">
      <div className="mx-auto max-w-[1400px]">
        <div className="mb-16 flex flex-col gap-4 md:mb-24 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <p className="overline mb-5 text-muted">Stills</p>
            <h2 className="font-display text-3xl font-extralight tracking-tight text-ink md:text-5xl">Selected Work</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/gallery"
              data-testid="home-gallery-link"
              className="group inline-flex items-center gap-3 self-start border-b border-ink/30 pb-1 transition-colors duration-500 hover:border-ink md:self-end"
            >
              <span className="overline">View the Gallery</span>
              <span className="transition-transform duration-500 group-hover:translate-x-1.5">↗</span>
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 md:gap-8">
          {teasers.map((p, i) => (
            <Reveal key={p.src} delay={i * 0.08} className={i === 1 ? "sm:mt-14" : ""}>
              <Link to="/gallery" data-cursor-hover className="group block overflow-hidden bg-paper2">
                <img
                  src={p.src}
                  alt={p.alt}
                  className="hover-media aspect-[3/4] w-full object-cover"
                />
                <span className="mt-3 block overline text-muted">{p.title}</span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <DelicateMarquee />
      <InMotion />
      <BehindScenes />
      <GalleryTeaser />
      <Approach />
    </>
  );
}

